import React, { Component, Fragment, ReactElement } from 'react';

import { FormattedDate, FormattedMessage } from 'react-intl';
import ReactMarkdown from 'react-markdown';
import Module from '../../../application/Service/Module';

import determineImgSrcFromList from '../../../infrastructure/utilities/helperfunctions/determineImgSrcFromList';

import AnchorLink from '../../atoms/AnchorLink';
import Head from '../../atoms/Head';
import SinglePublicationHeader from '../../atoms/SinglePublicationHeader';
import AudioPlayer from '../../molecules/AudioPlayer';
import SinglePublicationMetaContent from '../../molecules/SinglePublication/SinglePublicationMetaContent';
import SinglePublicationVideoContent from '../../molecules/SinglePublication/SinglePublicationVideoContent';
import NotFound from '../NotFound';

import imgUrls from '../../../domain/Publication/Fixture/publicationCardImgUrls';
import RouteGenerator from '../../../infrastructure/Navigation/RouteGenerator';
import { handleRouteComponentMount } from '../../../infrastructure/utilities/globalComponentMountOperations/globalComponentMountOperations';
import PageHeader from '../../atoms/PageHeader';
import { TLocale } from '../../../infrastructure/Type/Locale';
import * as style from './index.module.css';

export interface ISinglePublicationProps
{
    pageContext: {
        publicationDataDe: Record<string, any>;
        publicationDataEn: Record<string, any>;
        locale: TLocale;
        resourceNotFound: boolean;
    };

}

class SinglePublication extends Component<ISinglePublicationProps, any>
{
    public componentDidMount(): void
    {
        const module = Module.get();
        module.scroll && module.scroll(0, 0);
        handleRouteComponentMount();
    }

    public clipLongText = (text: string, limit: number): string | null =>
    {
        if (!text)
        {
            return null;
        }

        // @todo [refactor]
        if (!limit || typeof limit !== 'number')
        {
            return null;
        }

        const trimmedText = text.trim();

        if (trimmedText.length >= limit)
        {
            return trimmedText.substring(0, limit) + '...';
        }

        return trimmedText;
    };

    public generatePublishDate(date: number): ReactElement | null
    {
        if (!date)
        {
            return null;
        }

        return (
            <div
                style={{
                    lineHeight: '1.4rem',
                    fontWeight: 'bold',
                    display: 'flex',
                    flexDirection: 'row',
                }}
            >
                <FormattedMessage id="originally_published"/>
                &nbsp;
                <FormattedDate
                    value={date}
                    year="numeric"
                    month="long"
                    day="numeric"
                />
            </div>
        );
    }

    public render(): ReactElement
    {
        const {
            publicationDataDe,
            publicationDataEn,
            resourceNotFound,
            locale,
        } = this.props.pageContext;

        if (resourceNotFound)
        {
            return (
                <NotFound/>
            );
        }

        const localeBasedPublication = locale === 'en' ? publicationDataEn : publicationDataDe;

        const {
            audioSrc,
            title,
            publishingOrganizationName,
            publishToGlaubensfutterDate,
            articleBodyMarkdown,
            linkToOriginalWork,
            translatedBy,
            linkToPublisherOrganizationSite,
            videoSrc,
            originalAuthor,
            originalPublishDate,
            description,
            tags,
            image,
            estimatedReadingTime,
        } = localeBasedPublication;

        let audioContent = null;
        if (audioSrc)
        {
            audioContent = (
                <Fragment>
                    <h3><FormattedMessage id="audio_content"/></h3>
                    <div className={style.audio_player}>
                        <br/>
                        <AudioPlayer
                            audioSrc={audioSrc}
                        />
                    </div>
                </Fragment>
            );
        }

        const articleTitle = title;
        const metaTitle = 'Glaubensfutter' + (articleTitle ? ' - ' + articleTitle : '');

        return (
            <section>
                <Head
                    locale={this.props.pageContext.locale}
                    ogImage={image ?? RouteGenerator.forPublicUrl(determineImgSrcFromList(localeBasedPublication.title, imgUrls))}
                    ogTitle={metaTitle}
                    ogDescription={description}
                    ogUrl={RouteGenerator.forSinglePublicationPage(localeBasedPublication.slug, localeBasedPublication.id, locale)}
                ><title>{metaTitle}</title></Head>
                {image ? <SinglePublicationHeader
                    title={title}
                    showHr={true}
                    hrStyle={{
                        width: '50vw',
                        marginTop: '1rem',
                    }}
                    imageUrl={image}
                /> :
                    <PageHeader
                        title={title}
                        showHr={true}
                        rootElementClassName={style.singlePublicationHeadline}
                        hrStyle={{
                            width: '50vw',
                            marginTop: '1rem',
                        }}
                    />
                }
                <SinglePublicationMetaContent
                    titleDe={title}
                    titleEn={title}
                    descriptionDe={description}
                    descriptionEn={description}
                    publishingOrganizationName={publishingOrganizationName}
                    linkToPublisherOrganizationSite={linkToPublisherOrganizationSite}
                    originalAuthor={originalAuthor}
                    publishToGlaubensfutterDate={publishToGlaubensfutterDate}
                    clipLongText={this.clipLongText}
                    locale={locale}
                    tags={tags}
                    estimatedReadingTime={estimatedReadingTime}
                />
                <article className="article-reader">
                    <SinglePublicationVideoContent
                        title={title}
                        videoSrc={videoSrc}
                    />
                    {audioContent}
                    <ReactMarkdown>
                        {articleBodyMarkdown}
                    </ReactMarkdown>
                    <hr/>
                    <div className={style.belowArticleInfo}>
                        {this.generatePublishDate(originalPublishDate)}
                        <div style={{ lineHeight: '1.4rem', paddingTop: '.7rem', paddingBottom: '.7rem' }}>
                            <AnchorLink
                                href={linkToOriginalWork}
                                target="_blank"
                            >
                                <FormattedMessage id="link_to_original_project"/>
                            </AnchorLink>
                        </div>
                        {
                            translatedBy && (
                                <div
                                    style={{
                                        lineHeight: '1.4rem',
                                        fontWeight: 'bold',
                                        display: 'flex',
                                        flexDirection: 'row',
                                    }}
                                >
                                    <FormattedMessage tagName="div" id="translated_by"/>
                                    <div>&nbsp;{translatedBy}</div>
                                </div>
                            )
                        }
                        <div className="below-article-child" style={{ fontSize: '15px', marginTop: '1rem' }}>
                            <FormattedMessage id="views_disclaimer"/>
                        </div>
                    </div>

                </article>
            </section>
        );
    }
}

export default SinglePublication;
